import {
  Box,
  Card,
  HStack,
  Heading,
  SimpleGrid,
  Stack,
  Table,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  CohortFilter,
  Comparison,
  CustomerValueData,
  Quintile,
  getCustomerValue,
} from "../api/customer-value";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "../components/select";
import { useAppSelector, useLoading } from "../hooks";

const ACCENT = "#4988F5";
const ACCENT_LIGHT = "#4988F520";

const fmt = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const fmtDec = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const pct = (n: number) => `${Math.round(n * 100)}%`;

const cohortOptions = createListCollection({
  items: [
    { value: "all", label: "All time" },
    { value: "12m", label: "Last 12 months" },
    { value: "90d", label: "Last 90 days" },
  ],
});

export default function CustomerValuePage() {
  const slug = useAppSelector((s) => s.workspace.slug);
  const [data, setData] = React.useState<CustomerValueData | null>(null);
  const [isLoading, withLoading] = useLoading(true);
  const [since, setSince] = React.useState<CohortFilter>("all");

  React.useEffect(() => {
    if (!slug) return;
    withLoading(async () => {
      try {
        setData(await getCustomerValue(slug, since));
      } catch {
        // Ignore fetch errors; the page renders empty.
      }
    });
  }, [since, slug, withLoading]);

  return (
    <Page loading={isLoading}>
      <Stack flex="1">
        <PageHeader
          title="Customer Value"
          subtitle="Revenue distribution and customer segment analysis"
        >
          <SelectRoot
            size="xs"
            width="3xs"
            value={[since]}
            onValueChange={(e) => setSince(e.value[0] as CohortFilter)}
            collection={cohortOptions}
          >
            <SelectTrigger>
              <SelectValueText />
            </SelectTrigger>
            <SelectContent>
              {cohortOptions.items.map((option) => (
                <SelectItem key={option.value} item={option}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </PageHeader>
        <PageBody>
          {data && (
            <Stack gap="10">
              {data.comparison && (
                <HeroStats
                  multiplier={data.comparison.multiplier}
                  quintiles={data.quintiles}
                  predictability={data.predictability}
                />
              )}
              <QuintileChart quintiles={data.quintiles} />
              {data.comparison && (
                <ComparisonSection comparison={data.comparison} />
              )}
            </Stack>
          )}
        </PageBody>
      </Stack>
    </Page>
  );
}

function HeroStats({
  multiplier,
  predictability,
  quintiles,
}: {
  multiplier: number;
  predictability: number | null;
  quintiles: Quintile[];
}) {
  const topShare = quintiles[0]?.["revenue-share"] ?? 0;
  const totalCount = quintiles.reduce((s, q) => s + q["customer-count"], 0);

  return (
    <SimpleGrid columns={{ base: 1, md: 4 }} gap="4">
      <Card.Root
        style={{ background: "linear-gradient(135deg, #0F172A, #1E293B)" }}
      >
        <Card.Body p="6">
          <Stack gap="1">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              style={{
                letterSpacing: "0.1em",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              Value Gap
            </Text>
            <HStack align="baseline" gap="1">
              <Text
                fontWeight="bold"
                style={{ fontSize: "40px", lineHeight: 1, color: ACCENT }}
              >
                {multiplier.toFixed(1)}x
              </Text>
            </HStack>
            <Text fontSize="xs" style={{ color: "rgba(255,255,255,0.7)" }}>
              Top vs bottom 20%
            </Text>
          </Stack>
        </Card.Body>
      </Card.Root>
      <Card.Root>
        <Card.Body p="6">
          <Stack gap="1">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              color="fg.muted"
              style={{ letterSpacing: "0.1em" }}
            >
              Revenue Concentration
            </Text>
            <HStack align="baseline" gap="1">
              <Text
                fontWeight="bold"
                style={{ fontSize: "40px", lineHeight: 1, color: ACCENT }}
              >
                {pct(topShare)}
              </Text>
            </HStack>
            <Text fontSize="xs" color="fg.muted">
              From top 20% of customers
            </Text>
          </Stack>
        </Card.Body>
      </Card.Root>
      <Card.Root>
        <Card.Body p="6">
          <Stack gap="1">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              color="fg.muted"
              style={{ letterSpacing: "0.1em" }}
            >
              Customers Analyzed
            </Text>
            <HStack align="baseline" gap="1">
              <Text
                fontWeight="bold"
                style={{ fontSize: "40px", lineHeight: 1 }}
              >
                {totalCount.toLocaleString()}
              </Text>
            </HStack>
            <Text fontSize="xs" color="fg.muted">
              Across {quintiles.length} segments
            </Text>
          </Stack>
        </Card.Body>
      </Card.Root>
      <Card.Root>
        <Card.Body p="6">
          <Stack gap="1">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              textTransform="uppercase"
              color="fg.muted"
              style={{ letterSpacing: "0.1em" }}
            >
              LTV Predictability
            </Text>
            <HStack align="baseline" gap="1">
              <Text
                fontWeight="bold"
                style={{ fontSize: "40px", lineHeight: 1, color: ACCENT }}
              >
                {predictability != null ? predictability.toFixed(2) : "—"}
              </Text>
            </HStack>
            <Text fontSize="xs" color="fg.muted">
              First order rank vs lifetime spend
            </Text>
          </Stack>
        </Card.Body>
      </Card.Root>
    </SimpleGrid>
  );
}

function QuintileChart({ quintiles }: { quintiles: Quintile[] }) {
  const chartData = quintiles.map((q) => ({
    ...q,
    revenueSharePct: q["revenue-share"] * 100,
  }));

  return (
    <Stack gap="3">
      <Stack gap="0">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          style={{ letterSpacing: "0.08em", color: ACCENT }}
        >
          Revenue Concentration
        </Text>
        <Heading size="lg" fontWeight="semibold">
          Where your revenue comes from
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          Customers grouped into quintiles by total spend
        </Text>
      </Stack>
      <Card.Root>
        <Card.Body p="6">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 0, right: 40, left: 10, bottom: 0 }}
              barCategoryGap="24%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke="#E2E8F0"
              />
              <XAxis
                type="number"
                domain={[0, "auto"]}
                tickFormatter={(v: number) => `${v}%`}
                fontSize={12}
                stroke="#94A3B8"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={90}
                tick={{ fontSize: 13 }}
                stroke="#94A3B8"
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(73, 136, 245, 0.08)" }}
                formatter={(value: number) => [
                  `${value.toFixed(1)}%`,
                  "Revenue share",
                ]}
                contentStyle={{
                  borderRadius: "6px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  fontSize: "13px",
                }}
              />
              <Bar
                dataKey="revenueSharePct"
                radius={[0, 4, 4, 0]}
                fill={ACCENT}
              />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card.Root>
      <SimpleGrid columns={{ base: 2, md: 5 }} gap="3">
        {quintiles.map((q) => (
          <Box
            key={q.label}
            py="3"
            px="4"
            borderRadius="md"
            style={{ backgroundColor: ACCENT_LIGHT }}
          >
            <Text fontWeight="bold" style={{ color: ACCENT }}>
              {pct(q["revenue-share"])}
            </Text>
            <Text fontSize="xs" fontWeight="medium">
              {q.label}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {q["customer-count"].toLocaleString()} customers &middot;{" "}
              {fmt(q["avg-per-customer"])}/cust
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {fmt(q["total-discount"])} in discounts
            </Text>
          </Box>
        ))}
      </SimpleGrid>
    </Stack>
  );
}

interface ComparisonRowData {
  metric: string;
  top: string;
  bottom: string;
  diff: string;
}

function ComparisonSection({ comparison }: { comparison: Comparison }) {
  const { top, bottom, multiplier } = comparison;

  const rows: ComparisonRowData[] = [
    {
      metric: "Avg First Order",
      top: fmtDec(top["avg-first-order"]),
      bottom: fmtDec(bottom["avg-first-order"]),
      diff: `+${fmt(top["avg-first-order"] - bottom["avg-first-order"])}`,
    },
    {
      metric: "Days to 2nd Purchase",
      top:
        top["days-to-second-purchase"] != null
          ? `${top["days-to-second-purchase"]}`
          : "—",
      bottom:
        bottom["days-to-second-purchase"] != null
          ? `${bottom["days-to-second-purchase"]}`
          : "—",
      diff:
        top["days-to-second-purchase"] != null &&
        bottom["days-to-second-purchase"] != null
          ? `${Math.abs(top["days-to-second-purchase"] - bottom["days-to-second-purchase"])}d`
          : "—",
    },
    {
      metric: "Repeat Rate",
      top: pct(top["repeat-rate"]),
      bottom: pct(bottom["repeat-rate"]),
      diff: `+${Math.round((top["repeat-rate"] - bottom["repeat-rate"]) * 100)}pp`,
    },
    {
      metric: "Avg Orders",
      top: top["avg-orders"].toFixed(1),
      bottom: bottom["avg-orders"].toFixed(1),
      diff: `${(top["avg-orders"] / (bottom["avg-orders"] || 1)).toFixed(1)}x`,
    },
    {
      metric: "Avg 12-Mo LTV",
      top: fmtDec(top["avg-12-month-ltv"]),
      bottom: fmtDec(bottom["avg-12-month-ltv"]),
      diff: `${multiplier.toFixed(1)}x`,
    },
    {
      metric: "Avg Customer Age",
      top:
        top["avg-customer-age-days"] != null
          ? `${top["avg-customer-age-days"]}d`
          : "—",
      bottom:
        bottom["avg-customer-age-days"] != null
          ? `${bottom["avg-customer-age-days"]}d`
          : "—",
      diff:
        top["avg-customer-age-days"] != null &&
        bottom["avg-customer-age-days"] != null
          ? `${Math.abs(top["avg-customer-age-days"] - bottom["avg-customer-age-days"])}d`
          : "—",
    },
  ];

  return (
    <Stack gap="3">
      <Stack gap="0">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          style={{ letterSpacing: "0.08em", color: ACCENT }}
        >
          High-Value Customer Profile
        </Text>
        <Heading size="lg" fontWeight="semibold">
          What sets your best customers apart
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          Side-by-side comparison of your top 20% vs bottom 20% customers
        </Text>
      </Stack>
      <Card.Root>
        <Card.Body p="6">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader
                  fontSize="xs"
                  textTransform="uppercase"
                  color="fg.muted"
                  fontWeight="semibold"
                  borderBottomWidth="2"
                  style={{ letterSpacing: "0.05em" }}
                >
                  Metric
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textAlign="right"
                  fontSize="xs"
                  textTransform="uppercase"
                  fontWeight="semibold"
                  borderBottomWidth="2"
                  style={{ letterSpacing: "0.05em", color: ACCENT }}
                >
                  Top 20%
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textAlign="right"
                  fontSize="xs"
                  textTransform="uppercase"
                  color="fg.muted"
                  fontWeight="semibold"
                  borderBottomWidth="2"
                  style={{ letterSpacing: "0.05em" }}
                >
                  Bottom 20%
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textAlign="right"
                  fontSize="xs"
                  textTransform="uppercase"
                  color="fg.muted"
                  fontWeight="semibold"
                  borderBottomWidth="2"
                  style={{ letterSpacing: "0.05em" }}
                >
                  Difference
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.metric}>
                  <Table.Cell fontWeight="medium" py="3">
                    {row.metric}
                  </Table.Cell>
                  <Table.Cell
                    textAlign="right"
                    fontWeight="semibold"
                    py="3"
                    style={{ color: ACCENT }}
                  >
                    {row.top}
                  </Table.Cell>
                  <Table.Cell textAlign="right" py="3" fontWeight="semibold">
                    {row.bottom}
                  </Table.Cell>
                  <Table.Cell textAlign="right" py="3" color="fg.muted">
                    {row.diff}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
}
